import { GridCellProps } from '@progress/kendo-react-grid';
import { TreeListCellProps } from '@progress/kendo-react-treelist';
import { useRef } from 'react';
import { Tooltip, TooltipRefProps } from 'react-tooltip';
import { KwelloSparklineChart, KwelloSparklineMouseEvent } from '../charts/KwelloSparklineChart';

type SparklineCellProps = (TreeListCellProps | GridCellProps | undefined) & {
  blueLineLabel?: string;
  orangeLineLabel?: string;
  isSingleLine?: boolean;
  dataKey?: string;
};

const PRIMARY_COLOR = '#3EA9E9';
const SECONDARY_COLOR = '#E07528';

interface SparklineCellTooltipProps {
  primaryValue?: number;
  primaryLabel?: string;
  secondaryValue?: number;
  secondaryLabel?: string;
}

function SparklineCellTooltip({
  primaryValue,
  primaryLabel,
  secondaryValue,
  secondaryLabel,
}: SparklineCellTooltipProps) {
  return (
    <div className="cell-sparkline-tooltip">
      {primaryLabel && (
        <div className="cell-sparkline-row">
          <div className="cell-sparkline-tooltip-dot" style={{ backgroundColor: PRIMARY_COLOR }} />
          {primaryLabel}: {primaryValue ?? 0}
        </div>
      )}
      {secondaryLabel && (
        <div className="cell-sparkline-row">
          <div
            className="cell-sparkline-tooltip-dot"
            style={{ backgroundColor: SECONDARY_COLOR }}
          />
          {secondaryLabel}: {secondaryValue ?? 0}
        </div>
      )}
    </div>
  );
}

export function SparklineCell(props: SparklineCellProps) {
  const { dataKey = 'chartData' } = props;
  const tooltipRef = useRef<TooltipRefProps>(null);
  const responseDetailData =
    (props.dataItem?.[dataKey]?.groupedList[0]?.data as any[])?.map((item) => item.y as number) ||
    [];
  const responseData =
    (props.dataItem?.[dataKey]?.groupedList[1]?.data as any[])?.map((item) => item.y as number) ||
    [];

  const primaryData = !props.isSingleLine ? responseData : responseDetailData;
  const secondaryData = responseDetailData;

  const showTooltip = ({ x, y, index }: KwelloSparklineMouseEvent) => {
    if (tooltipRef) {
      tooltipRef.current?.open({
        place: 'top',
        position: {
          x,
          y,
        },
        content: (
          <SparklineCellTooltip
            primaryLabel={props.blueLineLabel}
            secondaryLabel={props.orangeLineLabel}
            primaryValue={primaryData[index] ?? 0}
            secondaryValue={secondaryData[index] ?? 0}
          />
        ),
      });
    }
  };

  const hideTooltip = () => {
    if (tooltipRef) {
      tooltipRef.current?.close();
    }
  };

  return (
    <td className="cell-lightweight-sparkline">
      <>
        <div className="cell-lightweight-sparkline-child">
          {primaryData.length > 0 && (
            <KwelloSparklineChart
              color={PRIMARY_COLOR}
              data={primaryData}
              onMouseEnterPoint={showTooltip}
              onMouseLeavePoint={hideTooltip}
            />
          )}
        </div>
        <div className="cell-lightweight-sparkline-child">
          {!props.isSingleLine && (
            <KwelloSparklineChart
              color={SECONDARY_COLOR}
              data={secondaryData}
              onMouseEnterPoint={showTooltip}
              onMouseLeavePoint={hideTooltip}
            />
          )}
        </div>
        <Tooltip ref={tooltipRef} positionStrategy="fixed" />
      </>
    </td>
  );
}
